import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { reelCredits, VIDEO_CREDIT_COST } from 'fym-shared/src/music24/music24Plans';
import { ASSET_SPECS } from 'fym-shared/src/music24/assetSpecs';
import {
  isThemeAvailable,
  needsFullVideoPlan,
  outputCredits,
  runCredits,
  STUDIO_THEME_IDS,
  STUDIO_THEMES,
  StudioFormat,
  StudioThemeId,
} from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../../components/primitives/Panel';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { useMusic24Capabilities } from '../../../hooks/useMusic24Capabilities';
import { useMusic24Plan } from '../../../hooks/useMusic24Plan';
import { useLicenseInfo } from '../../../../../redux/slices/license/hooks/hooks';
import { useUpgradePrompt } from '../../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../../config/music24PlanCapabilities';
import { useVideoCredits } from '../../credits/useVideoCredits';
import { useVideoCreditGate } from '../../credits/useVideoCreditGate';
import { MemberOwnerNote } from '../../credits/MemberOwnerNote';
import { studioFormatMessages } from '../../studio/studioThemeMessages';
import { useKitNavigation } from '../assets/useKitNavigation';
import { RevisionCard } from '../assets/revisionCards';
import { CanMakeRow } from './CanMakeRow';
import { overviewMessages } from './overviewMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Overview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CanStillMakePanelProps {
  accountId: number;
  item: CatalogItem;
  cards: RevisionCard[];
}

/** The groups of "Can still make", in design order (b1): the videos that cost the most first. */
const FORMAT_ORDER: ReadonlyArray<StudioFormat> = ['full', 'short', 'image'];

/**
 * The credits of one run of an asset type with its default files. Images cost 0. The AI reel has
 * no kit file: it costs what the Studio charges for a reel (`reelCredits`, QA finding F10).
 */
export function themeRunCredits(themeId: StudioThemeId): number {
  if (themeId === 'aiReel') {
    return reelCredits();
  }
  return runCredits(themeId, STUDIO_THEMES[themeId].outputs);
}

/**
 * The credits of each video file when one run makes several videos that each cost the same (the
 * countdown: 7, 3 and 1 days to go), else null. The Studio card shows that per-file price, so the
 * Overview says "1 credit each" (QA finding F10). Pure.
 */
export function themeCreditsEach(themeId: StudioThemeId): number | null {
  const { outputs, creditsPerRun } = STUDIO_THEMES[themeId];
  if (creditsPerRun !== undefined) {
    return null;
  }
  const videoCredits = outputCredits(themeId, outputs).filter((credits, index) => {
    const output = outputs[index];
    return output !== undefined && ASSET_SPECS[output.specId].kind === 'video' && credits > 0;
  });
  return videoCredits.length > 1 ? (videoCredits[0] ?? null) : null;
}

/**
 * Can still make (design b1, e5–e7): the asset types with no revision for this song, with their cost
 * (images free, short videos 1, full videos and the motion artwork 3), the credits left this month,
 * and "Open Studio" with the song picked. A full video on Free shows "Artist plan" and opens the
 * plans; a type that costs more than the balance opens the packs (owner) or the owner note (member).
 */
const CanStillMakePanelComponent: React.FC<CanStillMakePanelProps> = ({ accountId, item, cards }) => {
  const intl = useIntl();
  const { canRenderFullSongVideos } = useMusic24Capabilities();
  const { credits } = useVideoCredits({ accountId });
  const gate = useVideoCreditGate(accountId);
  const openUpgrade = useUpgradePrompt();
  const navigation = useKitNavigation(item);
  const plan = useMusic24Plan();
  const licenseInfo = useLicenseInfo();
  // QA finding F09: the trial has its own credit line. The ledger counts per UTC month, so a trial
  // that crosses the 1st gets the trial credits again on that day.
  const trialEndsAt = plan === 'trial' && licenseInfo.isTrialActive ? licenseInfo.trialEndsAt : null;
  const made = useMemo(() => new Set(cards.map((card) => card.themeId)), [cards]);
  const groups = useMemo(
    () =>
      FORMAT_ORDER.map((format) => ({
        format,
        themes: STUDIO_THEME_IDS.filter(
          (id) => STUDIO_THEMES[id].format === format && isThemeAvailable(id) && !made.has(id)
        ),
      })).filter((group) => group.themes.length > 0),
    [made]
  );
  const left = credits?.totalLeft ?? null;
  const reset = credits === null ? '' : intl.formatDate(new Date(credits.resetsAt), { day: 'numeric', month: 'short' });
  const share =
    credits?.limit === null || credits === null || credits.limit === 0 ? 0 : (credits.planLeft ?? 0) / credits.limit;

  return (
    <Panel as="section" data-testid="release-can-make">
      <PanelHead
        title={intl.formatMessage(overviewMessages.canMakeTitle)}
        subtitle={intl.formatMessage(overviewMessages.canMakeSubtitle)}
      />
      <PanelBody>
        {credits !== null && left !== null && (
          <>
            <span className={styles.creditLine}>
              {intl.formatMessage(
                trialEndsAt === null ? overviewMessages.creditsLeft : overviewMessages.creditsLeftTrial,
                {
                  count: left,
                }
              )}
            </span>
            <div className={styles.bar} aria-hidden="true">
              {/* The width is the data: the share of the month's plan credits that is left. */}
              <i className={styles.barFill} style={{ width: `${Math.round(share * 100)}%` }} />
            </div>
            <span className={styles.creditNote}>
              <CreditNote
                limit={credits.limit}
                reset={reset}
                resetsAt={new Date(credits.resetsAt)}
                trialEndsAt={trialEndsAt}
              />
            </span>
            {left === 0 &&
              (gate.canBuy ? (
                <SMLButton variant="primary" onClick={gate.offerCredits}>
                  {intl.formatMessage(overviewMessages.buyCredits)}
                </SMLButton>
              ) : (
                <MemberOwnerNote />
              ))}
          </>
        )}
        {groups.length === 0 && (
          <span className={styles.creditNote}>{intl.formatMessage(overviewMessages.everythingMade)}</span>
        )}
        {groups.map((group) => (
          <div key={group.format}>
            <div className={styles.makeGroup}>{intl.formatMessage(studioFormatMessages[group.format].plural)}</div>
            {group.themes.map((themeId) => {
              const cost = themeRunCredits(themeId);
              // The music video row opens its editor (spec 2026-10-09 D7, D11): every plan edits
              // and adds its own video; only "Make the video" in the editor needs Pro and credits.
              const opensEditor = themeId === 'musicVideo';
              const planLocked = !opensEditor && needsFullVideoPlan(themeId) && !canRenderFullSongVideos;
              return (
                <CanMakeRow
                  key={themeId}
                  themeId={themeId}
                  cost={cost}
                  costEach={themeCreditsEach(themeId)}
                  planLocked={planLocked}
                  creditLocked={!opensEditor && !planLocked && cost > 0 && gate.isShort(cost)}
                  onMake={() => navigation.openTheme(themeId)}
                  onUpgrade={() => openUpgrade(SMLUpgradeFeature.generic)}
                  onOfferCredits={gate.offerCredits}
                />
              );
            })}
          </div>
        ))}
      </PanelBody>
      <PanelFoot>
        <div className={styles.stack}>
          <SMLButton variant="primary" onClick={navigation.openStudio}>
            {intl.formatMessage(overviewMessages.openStudio)}
          </SMLButton>
          <span className={styles.studioNote}>
            {intl.formatMessage(overviewMessages.openStudioNote, { title: item.title })}
          </span>
        </div>
      </PanelFoot>
    </Panel>
  );
};

export const CanStillMakePanel = TypedMemo(CanStillMakePanelComponent);

interface CreditNoteProps {
  limit: number | null;
  /** The reset day of the month's plan credits, formatted. */
  reset: string;
  /** The reset time of the month's plan credits. */
  resetsAt: Date;
  /** The end of a running trial, else null. */
  trialEndsAt: Date | null;
}

/**
 * The line under the credit bar: the monthly plan count, or the trial count (QA finding F09). The
 * credit ledger counts per UTC month, so a trial that crosses the 1st says that a new month gives
 * more credits. The trial line says what a credit buys (QA 2026-10-28, skp-16, lit-13).
 */
const CreditNoteComponent: React.FC<CreditNoteProps> = ({ limit, reset, resetsAt, trialEndsAt }) => {
  const intl = useIntl();
  if (trialEndsAt !== null && limit !== null) {
    const date = intl.formatDate(trialEndsAt, { day: 'numeric', month: 'short' });
    const costs = { short: VIDEO_CREDIT_COST.short, full: VIDEO_CREDIT_COST.fullSong };
    if (resetsAt.getTime() < trialEndsAt.getTime()) {
      return <>{intl.formatMessage(overviewMessages.creditsTrialMore, { ...costs, limit, date, resetDate: reset })}</>;
    }
    return <>{intl.formatMessage(overviewMessages.creditsTrial, { ...costs, date })}</>;
  }
  return (
    <>
      {limit === null
        ? intl.formatMessage(overviewMessages.creditsReset, { date: reset })
        : intl.formatMessage(overviewMessages.creditsPlan, { limit, date: reset })}
    </>
  );
};

export const CreditNote = TypedMemo(CreditNoteComponent);
